import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "../lib/api";
import { Surface, Btn, Field, Input, Select, Textarea } from "./Primitives";
import { Plus, Check, Sparkle, ShieldCheck, EnvelopeSimple, Wrench, Eye, Translate } from "@phosphor-icons/react";

const AUTHORITIES = ["FAA", "EASA", "TCCA", "CAA-UK", "ANAC", "OTHER"];
const KINDS = ["ad", "sb", "service_letter", "advisory"];
const RECURRENCES = ["none", "hours", "cycles", "calendar"];
const STATUSES = ["open", "complied", "checked", "deferred", "not_applicable"];

const SEV_BADGE = {
  expired:     { label: "EXPIRED", cls: "bg-red-500/30 text-red-200 border-red-500/60" },
  urgent_7d:   { label: "URGENT",  cls: "bg-orange-500/30 text-orange-200 border-orange-500/60" },
  warning_30d: { label: "30D",     cls: "bg-amber/20 text-amber border-amber/60" },
};

function SeverityPill({ severity }) {
  if (!severity) return <span className="text-[10px] text-zinc-600 font-mono">—</span>;
  const s = SEV_BADGE[severity];
  return (
    <span className={`tag font-mono text-[10px] tracking-wider border ${s.cls}`} style={{ padding: "2px 8px" }}>
      {s.label}
    </span>
  );
}

function fmtDue(fc) {
  const bits = [];
  if (fc.due_in_hours != null) bits.push(fc.due_in_hours <= 0 ? `${Math.abs(fc.due_in_hours).toFixed(1)}h over` : `${fc.due_in_hours.toFixed(1)}h left`);
  if (fc.due_in_days != null) bits.push(fc.due_in_days <= 0 ? `${Math.abs(fc.due_in_days)}d ago` : `in ${fc.due_in_days}d`);
  if (fc.due_in_cycles != null) bits.push(fc.due_in_cycles <= 0 ? `${Math.abs(fc.due_in_cycles)} cyc over` : `${fc.due_in_cycles} cyc left`);
  return bits.length ? bits.join(" · ") : "—";
}

export default function AirworthinessTab({ aircraftId, isOwner }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [markingDone, setMarkingDone] = useState(null);
  const [doneForm, setDoneForm] = useState({});
  const [checking, setChecking] = useState(null);
  const [checkForm, setCheckForm] = useState({});
  const [notifying, setNotifying] = useState(null);
  const [notifyForm, setNotifyForm] = useState({ mechanic_email: "", mechanic_name: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [translating, setTranslating] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/aircraft/${aircraftId}/ad-compliance`);
    setRows(r.data);
  }, [aircraftId]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { expired: 0, urgent_7d: 0, warning_30d: 0, total: rows.length };
    rows.forEach((r) => { if (r.forecast?.severity) c[r.forecast.severity]++; });
    return c;
  }, [rows]);

  const saveAd = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const payload = {
        reference: editing.reference,
        title: editing.title,
        description: editing.description || null,
        authority: editing.authority,
        kind: editing.kind || "ad",
        issued_date: editing.issued_date || null,
        effective_date: editing.effective_date || null,
        recurrence_type: editing.recurrence_type || "none",
        recurrence_interval_hours: editing.recurrence_interval_hours ?? null,
        recurrence_interval_cycles: editing.recurrence_interval_cycles ?? null,
        recurrence_interval_calendar_months: editing.recurrence_interval_calendar_months ?? null,
        document_url: editing.document_url || null,
        notes: editing.notes || null,
        applicability: {
          manufacturer: editing.app_manufacturer || null,
          model: editing.app_model || null,
          model_prefix: editing.app_model_prefix || null,
          year_min: editing.app_year_min ?? null,
          year_max: editing.app_year_max ?? null,
          engine_manufacturer: editing.app_engine_manufacturer || null,
          engine_model: editing.app_engine_model || null,
          stc_reference: editing.app_stc_reference || null,
        },
      };
      // Strip nulls from applicability so backend keeps fields empty rather
      // than rejecting them.
      payload.applicability = Object.fromEntries(
        Object.entries(payload.applicability).filter(([, v]) => v !== null && v !== "")
      );
      await api.post("/ads", payload);
      setEditing(null);
      setToast("AD saved.");
      setTimeout(() => setToast(""), 4500);
      load();
    } catch (e) {
      setToast(`Save failed — ${e?.response?.data?.detail || e?.message || "error"}`);
      setTimeout(() => setToast(""), 4500);
    } finally {
      setBusy(false);
    }
  };

  const recompute = async () => {
    setBusy(true);
    try {
      await api.post("/ads/recompute", {});
      load();
    } finally {
      setBusy(false);
    }
  };

  const patchStatus = async (cid, status) => {
    await api.patch(`/ad-compliance/${cid}`, { status });
    load();
  };

  const submitMarkDone = async () => {
    if (!markingDone) return;
    setBusy(true);
    try {
      await api.post(`/ad-compliance/${markingDone.id}/mark-done`, doneForm);
      setMarkingDone(null);
      setDoneForm({});
      setToast("Compliance recorded.");
      setTimeout(() => setToast(""), 4500);
      load();
    } finally { setBusy(false); }
  };

  const submitCheck = async () => {
    if (!checking) return;
    setBusy(true);
    try {
      await api.post(`/ad-compliance/${checking.id}/check`, checkForm);
      setChecking(null);
      setCheckForm({});
      setToast("Check recorded.");
      setTimeout(() => setToast(""), 4500);
      load();
    } finally { setBusy(false); }
  };

  const submitNotifyMechanic = async () => {
    if (!notifying) return;
    if (!notifyForm.mechanic_email) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/ad-compliance/${notifying.id}/notify-mechanic`, notifyForm
      );
      setNotifying(null);
      setNotifyForm({ mechanic_email: "", mechanic_name: "", note: "" });
      setToast(r.data?.resend_id
        ? "Email sent to mechanic."
        : "Mechanic email saved — sending was skipped (Resend key missing).");
      setTimeout(() => setToast(""), 4500);
      load();
    } finally { setBusy(false); }
  };

  const addToInspection = async (row) => {
    setBusy(true);
    try {
      const r = await api.post(
        `/ad-compliance/${row.id}/add-to-inspection`, {}
      );
      setToast(r.data?.created
        ? "Added to maintenance program."
        : "Already on the maintenance program.");
      setTimeout(() => setToast(""), 4500);
      load();
    } finally { setBusy(false); }
  };

  const translateAds = async () => {
    if (!confirm("Translate all AD titles, descriptions and notes on this aircraft to English? Global FAA/EASA records are skipped — only tenant-imported entries are touched.")) return;
    setTranslating(true);
    try {
      const r = await api.post(`/aircraft/${aircraftId}/ads/translate-to-english`, {});
      const { translated = 0, scanned = 0 } = r.data || {};
      if (scanned === 0) {
        setToast("No tenant-imported AD text found on this aircraft to translate (global FAA/EASA records are already in English).");
      } else if (translated === 0) {
        setToast(`Scanned ${scanned} field(s) — everything is already in English. Nothing to change.`);
      } else {
        setToast(`Translated ${translated} field(s) (${scanned} scanned).`);
      }
      setTimeout(() => setToast(""), 5500);
      load();
    } catch (e) {
      setToast(`Translate failed — ${e?.response?.data?.detail || e?.message || "error"}`);
      setTimeout(() => setToast(""), 5500);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="space-y-6">
      <Surface className="p-6" data-testid="ad-summary">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Airworthiness directives</div>
            <div className="text-xs text-zinc-500 mt-1">Cross-aircraft AD applicability + recurring compliance for FAA / EASA / TCCA / CAA-UK.</div>
          </div>
          <div className="flex items-center gap-4">
            {["expired", "urgent_7d", "warning_30d"].map((sev) => (
              <div key={sev} className="text-right" data-testid={`ad-count-${sev}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{SEV_BADGE[sev].label}</div>
                <div className={`text-2xl font-mono ${sev === "expired" ? "text-red-400" : sev === "urgent_7d" ? "text-orange-400" : "text-amber"}`}>{counts[sev]}</div>
              </div>
            ))}
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Total</div>
              <div className="text-2xl font-mono">{counts.total}</div>
            </div>
          </div>
        </div>
        {isOwner && (
          <div className="mt-5 flex gap-2 flex-wrap">
            <Btn onClick={() => setEditing({ authority: "FAA", kind: "ad", recurrence_type: "none" })} data-testid="add-ad-btn"><Plus size={14} className="inline mr-1.5" />Add AD / SB</Btn>
            <Btn variant="ghost" onClick={recompute} disabled={busy} data-testid="recompute-ads-btn"><Sparkle size={14} className="inline mr-1.5" />{busy ? "Recomputing…" : "Re-match all"}</Btn>
            <Btn variant="ghost" onClick={translateAds} disabled={translating} data-testid="translate-ads-btn" title="Translate non-English AD titles, descriptions and notes to English"><Translate size={14} className="inline mr-1.5" />{translating ? "Translating…" : "Translate to English"}</Btn>
          </div>
        )}
      </Surface>

      <Surface className="p-6">
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[680px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-800">
              <th className="text-left py-2.5 px-3">Reference</th>
              <th className="text-left py-2.5 px-3">Authority</th>
              <th className="text-left py-2.5 px-3">Title</th>
              <th className="text-left py-2.5 px-3">Recurrence</th>
              <th className="text-left py-2.5 px-3">Status</th>
              <th className="text-left py-2.5 px-3">Forecast</th>
              <th className="text-right py-2.5 px-3"></th>
            </tr>
          </thead>
          <tbody data-testid="ad-table">
            {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-zinc-500">No applicable ADs yet. Add one above or wait for global ADs to match.</td></tr>}
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-zinc-900" data-testid={`ad-row-${row.id}`}>
                <td className="py-2.5 px-3 font-mono text-xs">{row.ad?.reference}</td>
                <td className="py-2.5 px-3 text-zinc-400">{row.ad?.authority}</td>
                <td className="py-2.5 px-3"><div className="text-zinc-200 truncate max-w-[260px]" title={row.ad?.title}>{row.ad?.title}</div>{row.ad?.scope === "global" && <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-mono">global</span>}</td>
                <td className="py-2.5 px-3 text-xs font-mono text-zinc-400">{row.ad?.recurrence_type === "calendar" ? `${row.ad.recurrence_interval_calendar_months} mo` : row.ad?.recurrence_type === "hours" ? `${row.ad.recurrence_interval_hours}h` : row.ad?.recurrence_type === "cycles" ? `${row.ad.recurrence_interval_cycles} cyc` : "one-shot"}</td>
                <td className="py-2.5 px-3">
                  {isOwner ? (
                    <Select value={row.status || "open"} onChange={(e) => patchStatus(row.id, e.target.value)} data-testid={`ad-status-${row.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </Select>
                  ) : <span className="text-xs uppercase tracking-wider text-zinc-400">{row.status}</span>}
                </td>
                <td className="py-2.5 px-3">
                  <div className="flex items-center gap-2">
                    <SeverityPill severity={row.forecast?.severity} />
                    <span className="text-xs text-zinc-400 font-mono">{fmtDue(row.forecast || {})}</span>
                  </div>
                </td>
                <td className="py-2.5 px-3 text-right">
                  {isOwner && (
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() => { setChecking(row); setCheckForm({ check_interval_months: row.check_interval_months || 12 }); }}
                        className="text-zinc-400 hover:text-amber text-[10px] uppercase tracking-[0.2em] font-mono"
                        title="Mark as reviewed (no compliance action taken)"
                        data-testid={`check-ad-${row.id}`}
                      >
                        <Eye size={11} className="inline mr-1" />Check
                      </button>
                      <button
                        onClick={() => { setNotifying(row); setNotifyForm({ mechanic_email: row.mechanic_email || "", mechanic_name: "", note: "" }); }}
                        className="text-zinc-400 hover:text-amber text-[10px] uppercase tracking-[0.2em] font-mono"
                        title="Email this AD to your mechanic"
                        data-testid={`notify-mechanic-${row.id}`}
                      >
                        <EnvelopeSimple size={11} className="inline mr-1" />Mechanic
                      </button>
                      <button
                        onClick={() => addToInspection(row)}
                        disabled={busy}
                        className="text-zinc-400 hover:text-amber text-[10px] uppercase tracking-[0.2em] font-mono disabled:opacity-40"
                        title="Add to the maintenance program for the next inspection"
                        data-testid={`add-to-inspection-${row.id}`}
                      >
                        <Wrench size={11} className="inline mr-1" />Inspect
                      </button>
                      <button onClick={() => { setMarkingDone(row); setDoneForm({}); }} className="text-amber hover:underline text-[10px] uppercase tracking-[0.2em] font-mono" data-testid={`mark-ad-done-${row.id}`}>
                        <Check size={11} className="inline mr-1" />Done
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Surface>

      {/* Add AD modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="ad-modal">
          <Surface className="w-full max-w-3xl p-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-medium mb-4 flex items-center gap-2"><ShieldCheck size={20} className="text-amber" />Add airworthiness directive</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Reference *"><Input value={editing.reference || ""} onChange={(e) => setEditing({ ...editing, reference: e.target.value })} placeholder="2024-12-05" data-testid="ad-ref-input" /></Field>
              <Field label="Authority *">
                <Select value={editing.authority || "FAA"} onChange={(e) => setEditing({ ...editing, authority: e.target.value })} data-testid="ad-authority-input">
                  {AUTHORITIES.map((a) => <option key={a} value={a}>{a}</option>)}
                </Select>
              </Field>
              <Field label="Kind">
                <Select value={editing.kind || "ad"} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                  {KINDS.map((k) => <option key={k} value={k}>{k.replace("_", " ")}</option>)}
                </Select>
              </Field>
              <Field label="Document URL"><Input value={editing.document_url || ""} onChange={(e) => setEditing({ ...editing, document_url: e.target.value })} placeholder="https://…" /></Field>
              <div className="col-span-2"><Field label="Title *"><Input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Wing strut inspection — recurring 100h" data-testid="ad-title-input" /></Field></div>
              <div className="col-span-2"><Field label="Description"><Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field></div>

              <Field label="Issued"><Input type="date" value={editing.issued_date || ""} onChange={(e) => setEditing({ ...editing, issued_date: e.target.value })} /></Field>
              <Field label="Effective"><Input type="date" value={editing.effective_date || ""} onChange={(e) => setEditing({ ...editing, effective_date: e.target.value })} /></Field>

              <Field label="Recurrence">
                <Select value={editing.recurrence_type || "none"} onChange={(e) => setEditing({ ...editing, recurrence_type: e.target.value })} data-testid="ad-recurrence-input">
                  {RECURRENCES.map((r) => <option key={r} value={r}>{r}</option>)}
                </Select>
              </Field>
              {editing.recurrence_type === "hours" && <Field label="Interval (hours)"><Input type="number" step="0.1" value={editing.recurrence_interval_hours ?? ""} onChange={(e) => setEditing({ ...editing, recurrence_interval_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} /></Field>}
              {editing.recurrence_type === "cycles" && <Field label="Interval (cycles)"><Input type="number" value={editing.recurrence_interval_cycles ?? ""} onChange={(e) => setEditing({ ...editing, recurrence_interval_cycles: e.target.value === "" ? null : parseInt(e.target.value, 10) })} /></Field>}
              {editing.recurrence_type === "calendar" && <Field label="Interval (months)"><Input type="number" value={editing.recurrence_interval_calendar_months ?? ""} onChange={(e) => setEditing({ ...editing, recurrence_interval_calendar_months: e.target.value === "" ? null : parseInt(e.target.value, 10) })} /></Field>}
            </div>

            <div className="mt-6 pt-5 border-t border-zinc-800">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-3">Applicability</div>
              <div className="text-xs text-zinc-500 mb-3">All declared criteria must match the aircraft. Leave blank for &quot;any&quot;. Must declare at least manufacturer or model.</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Manufacturer"><Input value={editing.app_manufacturer || ""} onChange={(e) => setEditing({ ...editing, app_manufacturer: e.target.value })} placeholder="Cessna" data-testid="ad-app-mfr" /></Field>
                <Field label="Model (exact)"><Input value={editing.app_model || ""} onChange={(e) => setEditing({ ...editing, app_model: e.target.value })} placeholder="172N" data-testid="ad-app-model" /></Field>
                <Field label="Model prefix"><Input value={editing.app_model_prefix || ""} onChange={(e) => setEditing({ ...editing, app_model_prefix: e.target.value })} placeholder="172" /></Field>
                <Field label="Year min"><Input type="number" value={editing.app_year_min ?? ""} onChange={(e) => setEditing({ ...editing, app_year_min: e.target.value === "" ? null : parseInt(e.target.value, 10) })} /></Field>
                <Field label="Year max"><Input type="number" value={editing.app_year_max ?? ""} onChange={(e) => setEditing({ ...editing, app_year_max: e.target.value === "" ? null : parseInt(e.target.value, 10) })} /></Field>
                <Field label="STC reference"><Input value={editing.app_stc_reference || ""} onChange={(e) => setEditing({ ...editing, app_stc_reference: e.target.value })} placeholder="STC SA09112SC" /></Field>
                <Field label="Engine manufacturer"><Input value={editing.app_engine_manufacturer || ""} onChange={(e) => setEditing({ ...editing, app_engine_manufacturer: e.target.value })} placeholder="Lycoming" /></Field>
                <Field label="Engine model"><Input value={editing.app_engine_model || ""} onChange={(e) => setEditing({ ...editing, app_engine_model: e.target.value })} placeholder="O-320" /></Field>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
              <Btn onClick={saveAd} disabled={busy || !editing.reference || !editing.title} data-testid="ad-save-btn">{busy ? "Saving…" : "Add AD"}</Btn>
            </div>
          </Surface>
        </div>
      )}

      {markingDone && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="ad-mark-done-modal">
          <Surface className="w-full max-w-md p-8">
            <h3 className="text-xl font-medium mb-2">Mark complied: {markingDone.ad?.reference}</h3>
            <div className="text-xs text-zinc-500 mb-5">Leave fields blank to auto-fill from today + current tach.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Date"><Input type="date" value={doneForm.date || ""} onChange={(e) => setDoneForm({ ...doneForm, date: e.target.value })} data-testid="ad-done-date" /></Field>
              <Field label="At hours (tach)"><Input type="number" step="0.1" value={doneForm.at_hours ?? ""} onChange={(e) => setDoneForm({ ...doneForm, at_hours: e.target.value === "" ? undefined : parseFloat(e.target.value) })} data-testid="ad-done-hours" /></Field>
              <div className="col-span-2"><Field label="Notes"><Input value={doneForm.notes || ""} onChange={(e) => setDoneForm({ ...doneForm, notes: e.target.value })} /></Field></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setMarkingDone(null)}>Cancel</Btn>
              <Btn onClick={submitMarkDone} disabled={busy} data-testid="ad-done-submit">{busy ? "Saving…" : "Record compliance"}</Btn>
            </div>
          </Surface>
        </div>
      )}

      {/* Phase 4F — Check (mark reviewed) modal */}
      {checking && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="ad-check-modal">
          <Surface className="w-full max-w-md p-8">
            <h3 className="text-xl font-medium mb-2">Mark as checked: {checking.ad?.reference}</h3>
            <div className="text-xs text-zinc-500 mb-5">Records that you reviewed this AD today. Optionally arm a calendar reminder to re-check.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Check date"><Input type="date" value={checkForm.date || ""} onChange={(e) => setCheckForm({ ...checkForm, date: e.target.value })} data-testid="ad-check-date" /></Field>
              <Field label="Re-check every (months)"><Input type="number" min={1} max={120} value={checkForm.check_interval_months ?? ""} onChange={(e) => setCheckForm({ ...checkForm, check_interval_months: e.target.value === "" ? undefined : parseInt(e.target.value, 10) })} data-testid="ad-check-interval" /></Field>
              <div className="col-span-2"><Field label="Notes"><Input value={checkForm.notes || ""} onChange={(e) => setCheckForm({ ...checkForm, notes: e.target.value })} /></Field></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setChecking(null)}>Cancel</Btn>
              <Btn onClick={submitCheck} disabled={busy} data-testid="ad-check-submit">Record check</Btn>
            </div>
          </Surface>
        </div>
      )}

      {/* Phase 4F — Notify mechanic modal */}
      {notifying && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="ad-notify-modal">
          <Surface className="w-full max-w-md p-8">
            <h3 className="text-xl font-medium mb-2">Email AD to mechanic</h3>
            <div className="text-xs text-zinc-500 mb-5">Sends {notifying.ad?.reference} — {notifying.ad?.title} — and a link to the document.</div>
            <div className="grid grid-cols-1 gap-3">
              <Field label="Mechanic email *"><Input type="email" value={notifyForm.mechanic_email} onChange={(e) => setNotifyForm({ ...notifyForm, mechanic_email: e.target.value })} placeholder="mechanic@example.com" data-testid="notify-mechanic-email" /></Field>
              <Field label="Mechanic name"><Input value={notifyForm.mechanic_name} onChange={(e) => setNotifyForm({ ...notifyForm, mechanic_name: e.target.value })} placeholder="Joe A&P" /></Field>
              <Field label="Note (optional)"><Textarea rows={3} value={notifyForm.note} onChange={(e) => setNotifyForm({ ...notifyForm, note: e.target.value })} placeholder="Please review at next visit." /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setNotifying(null)}>Cancel</Btn>
              <Btn onClick={submitNotifyMechanic} disabled={busy || !notifyForm.mechanic_email} data-testid="notify-mechanic-submit">{busy ? "Sending…" : "Send email"}</Btn>
            </div>
          </Surface>
        </div>
      )}

      {/* Phase 4F — Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-3 border border-amber/40 bg-[#1a1209] text-amber text-xs font-mono shadow-xl" data-testid="ad-action-toast">
          {toast}
        </div>
      )}
    </div>
  );
}
